<template>
    <b-modal
        lazy
        title-tag="h2"
        :title="details ? 'Edit Relationship' : 'New Relationship'"
        :visible="isOpen"
    >
        <b-form-group
            label-cols="3"
            label="Contact:"
            label-for="contact"
            class="d-flex align-items-center"
        >
            <span v-if="contactData">{{
                contactData.label || contactData.name
            }}</span>
        </b-form-group>

        <b-form-group label-cols="3" label="Relation:" label-for="relation">
            <b-form-select
                class="capitalize"
                :options="typeOptions"
                v-model="type"
                :disabled="isSaving"
                :class="{
                    'border-danger': formError.includes('type')
                }"
            />
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Name:"
            label-for="name"
            class="d-flex align-items-center"
        >
            <span v-if="details">{{ details.name }}</span>
            <v-entity-select
                v-else
                placeholder="Name (required)"
                v-model="selectedEntity"
                label="fullname"
                :key="entityKey"
                :disabled="isSaving || !type"
                :relation-type="type ? type.id : null"
                :contact-type="isCompany ? 'business' : 'actor'"
                :show-add-contact="showAddNew('3')"
                :show-add-company="showAddNew('4')"
                :show-add-patient="showAddNew('1')"
                :class="{
                    'border-danger': formError.includes('selectedEntity')
                }"
            />
        </b-form-group>

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button-group v-if="!isSaving && !details">
                <b-dropdown
                    v-if="!continues"
                    @click="saveDetails(false)"
                    left
                    split
                    text="Save + Done"
                    variant="primary"
                >
                    <b-dropdown-item @click="saveDetails(true)"
                        >Save + Add New</b-dropdown-item
                    >
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="saveDetails(true)"
                    left
                    split
                    text="Save + Add New"
                    variant="primary"
                >
                    <b-dropdown-item @click="saveDetails(false)"
                        >Save + Done</b-dropdown-item
                    >
                </b-dropdown>
            </b-button-group>
            <b-button
                v-else-if="!isSaving && details"
                variant="primary"
                @click="saveDetails"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { forEach, remove } from "lodash";
import Loader from "@/module/commons/loader";
import vEntitySelect from "@/components/contact-relationships/relationship-select";
import Relationships from "@/services/contact-relationships.js";

// 1 - Patient, 2 - Client, 3 - Contact/Actor, 4 - Company/Business
const MODULE_CODE = {
    patient: 1,
    client: 2,
    contact: 3,
    actor: 3,
    company: 4,
    business: 4
};

export default {
    name: "modalPatientRelatiohsipSave",
    components: {
        vEntitySelect,
        Loader
    },
    props: {
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            isOpen: false,
            isSaving: false,
            patient: null,
            clientName: null,
            type: null,
            oldType: null,
            oldClient: [],
            typeOptions: [],
            rawOptions: [],
            details: null,
            showMenu: false,
            showMenuClient: false,
            showMenuContactCompany: false,
            showMenuAll: false,
            selectedEntity: null,
            isWellnessPlan: false,
            showAlert: false,
            formError: [],
            inputNameNoType: null,

            entityOptions: [],
            isPreloading: false,
            entityKey: 0,
            continues: false
        };
    },
    computed: {
        contactData() {
            return this.$store.getters["contact/contactDetails"];
        },
        isCompany() {
            return this.$route.name === "contact_company";
        }
    },
    watch: {
        type(value) {
            if (value) {
                this.selectedEntity = null;
                this.entityKey++;
            }
        }
    },
    methods: {
        async open(relationship) {
            this.typeOptions = await this.generateRelationshipTypes();

            if (relationship) {
                this.oldType = _.clone(this.type);
                this.details = relationship;
            }

            if (relationship && this.typeOptions) {
                this.setType(relationship);
            }

            this.isOpen = true;
        },
        setType(relationship) {
            let self = this;

            // remove unrelated type for the selected entity
            remove(self.typeOptions, function (option) {
                return (
                    option.value &&
                    !self.checkTypeMatch(
                        option.value,
                        MODULE_CODE[self.details.entity_type]
                    )
                );
            });

            forEach(self.typeOptions, function (type) {
                if (type.value && type.value.id === relationship.relation_id) {
                    self.type = type.value;
                }
            });
        },
        showAddNew(moduleCode) {
            if (this.type) {
                return this.checkTypeMatch(this.type, moduleCode);
            }

            return false;
        },
        checkTypeMatch(type, moduleCode) {
            let modules = type.modules;
            let contactType = this.isCompany ? "4" : "3";

            let toMatch1 = contactType + ":" + moduleCode;
            let toMatch2 = moduleCode + ":" + contactType;

            return modules.includes(toMatch1) || modules.includes(toMatch2);
        },
        processOldDetails(relationship) {
            let data = _.clone(relationship);
            return {
                contact_type: data.entity_type,
                fullname: data.name,
                id: data.id,
                patient_name: data.name,
                type: data.relation_id
            };
        },

        isShowContactsOnly() {
            if (this.showNameOption) {
                let trimedText = this.showNameOption.replace(" ", "");
                return trimedText == "1:3,1:4" || trimedText == "1:4,1:3";
            }

            return false;
        },
        close() {
            this.isOpen = false;
            this.isSaving = false;
        },
        async generateRelationshipTypes() {
            const rawTypes = await Relationships.getTypes({
                contact_type: this.isCompany ? "company" : "actor"
            });
            let result = [];

            if (rawTypes.message.code === 200) {
                this.rawOptions = rawTypes.resources;

                for (const type of rawTypes.resources) {
                    result.push({
                        text: this.getTypeText(type),
                        value: { id: type.id, modules: type.modules }
                    });
                }
            }

            result = result.sort((a, b) => (a.text > b.text ? 1 : -1));
            result.unshift({ text: "Choose Relationship Type", value: null });
            return result;
        },
        getTypeText(type) {
            let text = type.value2;

            if (!this.isCompany) {
                if (type.modules.includes("3:4")) {
                    text = type.value;
                }
            }

            return text;
        },
        saveDetails(addNew) {
            this.formError = [];
            this.continues = addNew;

            if (this.type == null) {
                this.inputNameNoType = null;
                this.formError.push("type");
                this.formError.push("inputNameNoType");
            }

            if (
                (this.selectedEntity == null ||
                    !_.isObject(this.selectedEntity)) &&
                this.details == null
            ) {
                this.formError.push("selectedEntity");
                this.formError.push("inputNameNoType");
            }

            if (this.formError.length > 0) {
                return false;
            }

            if (this.details) {
                this.saveEdit();
            } else {
                this.saveNew(addNew);
            }
        },
        async saveNew(addNew) {
            try {
                this.isSaving = true;
                let response = null;
                let params = {
                    entity_id: this.selectedEntity.id,
                    entity_type: this.selectedEntity.contact_type,
                    relation_type: this.type.id
                };

                if (this.isCompany) {
                    params.business_id = this.contactData.id;
                    response = await Relationships.createCompanyRelationship(
                        params
                    );
                } else {
                    params.actor_id = this.contactData.id;
                    response = await Relationships.createContactRelationship(
                        params
                    );
                }

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this._message("Relationship successfully added.");
                    this.$emit("success");
                } else {
                    this._message(
                        "Error : Unable to add relationship.",
                        "danger"
                    );
                }
                addNew ? this.resetData() : this.close();
            } catch (e) {
                this._message("Error : Unable to add relationship.", "danger");
                addNew ? this.resetData() : this.close();
            }
        },
        async saveEdit() {
            try {
                this.isSaving = true;
                let response = null;

                let params = {
                    entity_id: this.details.id,
                    entity_type: this.details.entity_type,
                    relation_type: this.type.id,
                    old_entity_id: this.details.id,
                    old_relation_type: this.details.relation_id
                };

                if (this.isCompany) {
                    params.business_id = this.contactData.id;
                    response = await Relationships.updateCompanyRelationship(
                        params
                    );
                } else {
                    params.actor_id = this.contactData.id;
                    response = await Relationships.updateContactRelationship(
                        params
                    );
                }

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this._message("Relationship successfully updated.");
                    this.$emit("success");
                } else {
                    this._message(
                        "Error : Unable to update relationship.",
                        "danger"
                    );
                }
                this.close();
            } catch (e) {
                this._message(
                    "Error : Unable to update relationship.",
                    "danger"
                );
                this.close();
            }
        },
        resetData() {
            this.isSaving = false;
            this.type = null;
            this.selectedEntity = null;
        }
    }
};
</script>

<style scoped>
.capitalize {
    text-transform: capitalize;
}
</style>
